import { useCallback, useEffect, useState } from 'react';
import type {
  CapabilityCode,
  SafetyApprovalAttachment,
  WorkOrderForm344,
} from '@railroute/contracts/core';
import {
  ApiError,
  fetchForm344,
  fetchSafetyApprovalAttachment,
  requestWorkOrderApproval,
  signingAuthorizationUrl,
} from '../api';
import { mayApproveWorkOrder } from '../sections';
import { Form344Sheet } from '../form344/Form344Sheet';

// Наряд по номеру: лист приложения 1 к Правилам № 344 для просмотра и печати.
//
// Действие здесь ровно одно — согласование наряда (issue #445): согласующий
// работает за компьютером у пульта, и согласовывать с телефона ему неудобно.
// Ничего другого раздел не редактирует и не подписывает.
//
// СОГЛАСОВАНИЕ — ПОДПИСАННЫЙ ДОКУМЕНТ, А НЕ ОТМЕТКА (решение владельца
// 24.09.2026), и потому кнопка не «согласовывает», а уводит подписывать:
// сервер фиксирует канон и хеш документа, браузер уходит к провайдеру ЭЦП, и
// согласованным наряд становится после того, как подпись принята. Показать
// на этом экране «согласовано» сразу по нажатию значило бы напечатать
// разрешение, которого ещё никто не дал.
//
// Кнопка держится на праве, названном в `sections.ts` рядом с правами
// разделов, а не выписанном здесь: второй модели прав в панели нет, и код
// права, живущий в компоненте, разошёлся бы с перечнем молча.
//
// Управление раздела помечено `screen-only`: на бумагу идёт лист, и ничего
// кроме листа. Печатает сам браузер, в PDF сохраняет тоже он.

export function WorkOrderSection({
  token,
  organizationId,
  capabilities,
  initialNumber = null,
  onUnauthorized,
}: {
  token: string;
  organizationId: string;
  capabilities: readonly CapabilityCode[];
  /** Номер, с которым раздел открыт из журнала инструктажа (решение Штаба 30.09.2026). */
  initialNumber?: string | null;
  onUnauthorized: () => void;
}) {
  const [orderNumber, setOrderNumber] = useState(initialNumber ?? '');
  const [form, setForm] = useState<WorkOrderForm344 | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [approving, setApproving] = useState(false);

  const mayApprove = mayApproveWorkOrder(capabilities);

  const open = useCallback(async () => {
    if (orderNumber.trim().length === 0 || busy) return;
    setBusy(true);
    setError(null);
    try {
      setForm(await fetchForm344(orderNumber.trim(), token, organizationId));
    } catch (e) {
      setForm(null);
      // Истёкший токен — не поломка, а «войдите заново»: обновления в панели
      // нет намеренно, её открывают на время разбора.
      if (e instanceof ApiError && e.status === 401) onUnauthorized();
      setError(e instanceof Error ? e.message : 'Наряд не открылся');
    } finally {
      setBusy(false);
    }
  }, [token, organizationId, orderNumber, busy, onUnauthorized]);

  // Открыт из журнала инструктажа — лист наряда показывается сразу, без
  // второго нажатия. Номер меняется — лист перечитывается.
  useEffect(() => {
    if (!initialNumber) return;
    setOrderNumber(initialNumber);
    let cancelled = false;
    setBusy(true);
    setError(null);
    fetchForm344(initialNumber, token, organizationId)
      .then((loaded) => {
        if (!cancelled) setForm(loaded);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setForm(null);
        if (e instanceof ApiError && e.status === 401) onUnauthorized();
        setError(e instanceof Error ? e.message : 'Наряд не открылся');
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialNumber, token, organizationId]);

  /**
   * Issue #469: открыть приложенный к согласованию документ.
   *
   * Байты приходят строкой base64 и открываются как временный объект браузера
   * в новой вкладке. Ссылки на файл в API нет намеренно: приложение — часть
   * документа наряда, и отдавать его по адресу, который можно переслать, значило
   * бы открыть чужой бригаде персональные данные соседнего наряда. Здесь оно
   * отдаётся ровно тому, кто уже открыл лист и предъявил токен.
   *
   * Объект освобождается не сразу: вкладка ещё не успела его прочитать, и
   * немедленный `revokeObjectURL` закрыл бы файл раньше, чем он открылся.
   */
  const openAttachment = useCallback(
    (attachment: SafetyApprovalAttachment) => {
      void (async () => {
        setError(null);
        try {
          const content = await fetchSafetyApprovalAttachment(attachment.id, token, organizationId);
          const bytes = Uint8Array.from(atob(content.contentBase64), (char) => char.charCodeAt(0));
          const url = URL.createObjectURL(new Blob([bytes], { type: content.mimeType }));
          window.open(url, '_blank', 'noopener');
          window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (e) {
          if (e instanceof ApiError && e.status === 401) onUnauthorized();
          setError(e instanceof Error ? e.message : 'Приложение не открылось');
        }
      })();
    },
    [token, organizationId, onUnauthorized],
  );

  const approve = useCallback(async () => {
    if (!form || approving) return;
    setApproving(true);
    setError(null);
    try {
      // Шаг 1: сервер фиксирует канон и хеш документа согласования. Повторное
      // нажатие возвращает тот же документ, а не заводит второй.
      const { document } = await requestWorkOrderApproval(form.workOrder.id, token, organizationId);
      // Шаг 2: уход к провайдеру ЭЦП за подписью под этот хеш. Лист здесь НЕ
      // перерисовывается: подписи ещё нет, и дорисовать фамилию с датой
      // значило бы показать разрешение, которого никто не дал.
      const url = await signingAuthorizationUrl(document.id, token, organizationId);
      window.location.assign(url);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) onUnauthorized();
      setError(e instanceof Error ? e.message : 'Согласование не подписано');
      setApproving(false);
    }
  }, [form, approving, token, organizationId, onUnauthorized]);

  // Согласовать можно наряд, который ещё не согласован и ещё не допущен, —
  // те же два условия, по которым откажет сервер. Кнопка, ведущая в отказ,
  // хуже отсутствующей: она обещает работу, которой нет.
  const approvable =
    !!form && form.operationalApproval.approvedAt === null && form.workOrder.status === 'ISSUED';

  // Документ уже заведён, но не подписан — значит согласующий на полпути, и
  // кнопка обязана сказать об этом: «согласовать» на ней выглядело бы как
  // начало работы, которая уже начата.
  const resuming = !!form && form.operationalApproval.documentId !== null && approvable;

  return (
    <>
      <div className="section__controls screen-only">
        <input
          className="ui-input section__search"
          value={orderNumber}
          onChange={(event) => setOrderNumber(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') void open();
          }}
          placeholder="Номер наряда"
        />
        <button className="ui-btn ui-btn--primary" onClick={() => void open()} disabled={busy}>
          {busy ? 'Открываю…' : 'Открыть'}
        </button>
        <button className="ui-btn ui-btn--ghost" onClick={() => window.print()} disabled={!form}>
          Печать / PDF
        </button>
        {mayApprove ? (
          <button
            className="ui-btn ui-btn--primary"
            onClick={() => void approve()}
            disabled={!approvable || approving}
          >
            {approving
              ? 'Открываю подпись…'
              : resuming
                ? 'Продолжить подписание'
                : 'Согласовать наряд'}
          </button>
        ) : null}
      </div>
      {error ? <p className="ui-error panel__error screen-only">{error}</p> : null}
      {form ? <Form344Sheet form={form} onOpenAttachment={openAttachment} /> : null}
    </>
  );
}
